import React from "react";
import { Check, Clock, ShieldCheck, ClipboardList, Paintbrush, Hammer, Search, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const timelineData = [{
  title: "Consultation & Quote",
  duration: "Day 1",
  description: "We begin with a thorough site assessment to understand your vision and needs.",
  checklist: ["Site assessment & measurement", "Professional colour consultation", "Detailed, transparent quote"],
  icon: ClipboardList
}, {
  title: "Preparation & Protection",
  duration: "Day 1-2",
  description: "Meticulous preparation is the key to a flawless, long-lasting finish.",
  checklist: ["Furniture & floor covering", "Surface cleaning & sanding", "Minor repairs & patching"],
  icon: Hammer
}, {
  title: "Execution & Application",
  duration: "Day 3-5",
  description: "Our expert team applies premium paints with precision and care.",
  checklist: ["Priming bare surfaces", "Premium low-VOC paint application", "Detailed trim & woodwork"],
  icon: Paintbrush
}, {
  title: "Inspection & Handover",
  duration: "Final Day",
  description: "We ensure everything is perfect before we consider the job done.",
  checklist: ["Final walkthrough with client", "Touch-ups if required", "Complete site cleanup"],
  badge: "Quality Guaranteed",
  icon: ShieldCheck
}];
interface TimelineItemProps {
  item: typeof timelineData[0];
  index: number;
  total: number;
}
function TimelineItem({
  item,
  index,
  total
}: TimelineItemProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  const isLast = index === total - 1;
  return <div ref={ref} data-index={index} className={cn("group relative grid grid-cols-[auto_1fr] md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-12 items-start", !isLast && "pb-12 md:pb-16")}>
      {}
      <div className="md:hidden absolute left-[19px] top-0 bottom-0 w-px bg-border/60 -z-10">
        <div className={cn("absolute top-0 left-0 w-full bg-primary transition-all duration-1000 ease-out origin-top", isVisible ? "h-full" : "h-0")} />
      </div>

      {}
      <div className={cn("hidden md:flex flex-col justify-start pt-2", isEven ? "items-end text-right" : "items-start opacity-0 pointer-events-none")}>
        {isEven && <TimelineCard item={item} isVisible={isVisible} align="right" />}
      </div>

      {}
      <div className="relative flex flex-col items-center h-full">
        {}
        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-border/60 -z-10">
           <div className={cn("absolute top-0 left-0 w-full bg-primary transition-all duration-1000 ease-out origin-top", isVisible ? "h-full" : "h-0")} />
        </div>

        {}
        <div className={cn("relative z-10 flex items-center justify-center size-10 rounded-full border-2 shadow-sm transition-all duration-500 delay-150", isVisible ? "border-primary bg-primary text-primary-foreground scale-110 shadow-primary/25" : "border-border bg-background text-muted-foreground")}>
          <item.icon className="size-5" />
        </div>
      </div>

      {}
      <div className={cn("flex flex-col pt-2", "md:items-start")}>
        {}
        <div className="md:hidden w-full">
          <TimelineCard item={item} isVisible={isVisible} align="left" />
        </div>

        {}
        <div className="hidden md:block w-full">
          {!isEven && <TimelineCard item={item} isVisible={isVisible} align="left" />}
        </div>
      </div>
    </div>;
}
function TimelineCard({
  item,
  isVisible,
  align
}: {
  item: typeof timelineData[0];
  isVisible: boolean;
  align: "left" | "right";
}) {
  return <div className={cn("w-full transition-all duration-700 ease-out transform", isVisible ? "opacity-100 translate-y-0 translate-x-0" : cn("opacity-0 translate-y-8", align === "left" ? "md:translate-x-8" : "md:-translate-x-8"))}>
      <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden bg-card/50 backdrop-blur-sm">
        <div className={cn("absolute top-0 w-1 h-full bg-primary/10", align === "right" ? "right-0" : "left-0")} />
        
        <CardHeader className="pb-3">
          <div className={cn("flex flex-col gap-3", align === "right" ? "md:items-end" : "md:items-start")}>
            <div className="flex items-center gap-3">
              {align === "right" && <Badge variant="secondary" className="font-mono text-xs tracking-wider uppercase">{item.duration}</Badge>}
              <CardTitle className="text-xl md:text-2xl text-foreground">{item.title}</CardTitle>
              {align === "left" && <Badge variant="secondary" className="font-mono text-xs tracking-wider uppercase">{item.duration}</Badge>}
            </div>
            <CardDescription className={cn("text-base", align === "right" ? "md:text-right" : "md:text-left")}>
              {item.description}
            </CardDescription>
          </div>
        </CardHeader>
        
        <Separator className="bg-border/50" />
        
        <CardContent className="pt-4">
          <ul className={cn("space-y-2", align === "right" ? "md:items-end" : "md:items-start", "flex flex-col")}>
            {item.checklist.map((check, i) => <li key={i} className={cn("flex items-center gap-2 text-sm text-muted-foreground", align === "right" && "md:flex-row-reverse md:text-right")}>
                <div className="flex-shrink-0 size-5 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Check className="size-3" />
                </div>
                <span>{check}</span>
              </li>)}
          </ul>

          {item.badge && <div className={cn("mt-6 flex", align === "right" ? "md:justify-end" : "md:justify-start")}>
              <Badge className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-1.5 text-sm gap-1.5 shadow-sm">
                <Star className="size-3.5 fill-current" />
                {item.badge}
              </Badge>
            </div>}
        </CardContent>
      </Card>
    </div>;
}
export default function ProjectManagementTimeline() {
  return <section id="project-management-timeline" className="py-24 bg-background relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-muted/30 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-muted/30 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-24">
          <Badge variant="outline" className="mb-4 border-primary/20 text-primary bg-primary/5">
            Our Process
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6">
            From Consultation to Completion
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            A detailed look at the sustainable practices and professional project management that define ANV Painting.
            We ensure every step is transparent, efficient, and executed to the highest standard.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {timelineData.map((item, index) => <TimelineItem key={index} item={item} index={index} total={timelineData.length} />)}
        </div>
      </div>
    </section>;
}